import { Dialog, useDialogStore } from '@ariakit/react/dialog';
import { FocusScope } from '@react-aria/focus';
import cn from 'classnames';
import { useEffect, useState } from 'react';
import { useTimeout } from '#hooks/useTimeout.ts';
import { ModalProvider } from './ModalContext.tsx';
import styles from './Modal.module.css';

type Props = Omit<React.ComponentPropsWithRef<typeof Dialog>, 'store'> & {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  /** If true, clicking outside of the modal will cause it to call onClose */
  shouldCloseOnOverlayClick?: boolean;
  /** Whether to use FocusScope when determining the element to focus after close. */
  shouldRestoreFocusViaFocusScope?: boolean;
};

/*
 * TODO: animate open and close transition
 */
export function Modal({
  children,
  isOpen,
  onClose,
  shouldCloseOnOverlayClick,
  shouldRestoreFocusViaFocusScope,
  className,
  ...props
}: Props) {
  const [flash, setFlash] = useState(false);
  const dialog = useDialogStore({ open: isOpen });
  const { start: disableFlashAfterDelay } = useTimeout(() => setFlash(false), Number.parseInt(styles.duration, 10));

  // Set a timeout to turn flash back off
  useEffect(() => {
    if (flash) {
      disableFlashAfterDelay();
    }
  }, [flash, disableFlashAfterDelay]);

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog
      onClose={onClose}
      className={cn(className, styles.Modal, { [styles.Modal___backgroundClicked]: flash })}
      {...props}
      hideOnInteractOutside={(e) => {
        // Close the modal if the shouldCloseOnOverlayClick prop is true
        if (shouldCloseOnOverlayClick) {
          return true;
        }
        // Otherwise, give a little animation to let the user know they need to deal with the modal
        setFlash(true);
        e.preventDefault();
        return false;
      }}
      store={dialog}
      autoFocusOnHide={!shouldRestoreFocusViaFocusScope}
      backdrop={<div className={styles.Modal_overlay} data-testid="modal-overlay" />}
    >
      <FocusScope restoreFocus={shouldRestoreFocusViaFocusScope}>
        <ModalProvider closeModal={onClose}>{children}</ModalProvider>
      </FocusScope>
    </Dialog>
  );
}
